import type { SoundClip, SoundSpec, SoundVariant } from '../themes/types';
import { playTone } from './audio';
import { noteToFreq } from './notes';
import { playSample } from './samplePlayer';

// Spec-driven audio playback for custom themes. Consumers pass the full
// SoundSpec + a kind; this module handles variant selection (sequential
// cursor / random) and dispatches each variant to `playTone` under the
// hood so the synth stays the single audio path.

const CHORD_STAGGER_MS = 90;
const CHORD_DURATION_MS = 240;
const NOTE_DURATION_MS = 220;
const CHORD_GAIN = 0.18;
const NOTE_GAIN = 0.2;

// WeakMap so sequential cursors don't leak between test spec instances and
// tests can construct a fresh spec to reset state.
const sequentialCursors = new WeakMap<SoundClip, number>();

export function __resetPlayFromSpecCursors(): void {
  // WeakMap has no clear(); tests instantiate fresh SoundClip refs to
  // reset. This helper is a no-op used by tests to document the pattern.
}

export function playFromSpec(spec: SoundSpec, kind: 'tap' | 'celebration'): void {
  const clip = kind === 'tap' ? spec.tap : spec.celebration;
  const variant = pickVariant(clip);
  if (!variant) return;
  playVariant(variant);
}

function pickVariant(clip: SoundClip): SoundVariant | undefined {
  const { variants, pickMode } = clip;
  if (variants.length === 0) return undefined;
  if (pickMode === 'random') {
    // eslint-disable-next-line sonarjs/pseudo-random -- picking a sound variant, not a security decision
    const idx = Math.floor(Math.random() * variants.length);
    return variants[idx];
  }
  const cursor = sequentialCursors.get(clip) ?? 0;
  const variant = variants[cursor % variants.length];
  sequentialCursors.set(clip, cursor + 1);
  return variant;
}

function playVariant(variant: SoundVariant): void {
  switch (variant.kind) {
    case 'chord': {
      if (variant.notes.length === 0) return;
      variant.notes.forEach((note, idx) => {
        playTone(
          noteToFreq(note),
          CHORD_DURATION_MS,
          idx * CHORD_STAGGER_MS,
          CHORD_GAIN,
          'triangle',
        );
      });
      return;
    }
    case 'note': {
      playTone(noteToFreq(variant.note), NOTE_DURATION_MS, 0, NOTE_GAIN, 'triangle');
      return;
    }
    case 'sample': {
      playSample(variant.url, { volume: NOTE_GAIN });
      return;
    }
  }
}
